import { ToolDialog, ToolSummary, ToolSearch, ToolButton, ToolEmpty, ToolFilters } from "./ToolWorkspace";
import { HeaderIcon } from "./AppHeader";
import "./siteWorkspace.css";
import { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { fbList, fbUpdate } from "../hooks/useFirebase";
import { toast } from "../hooks/useToast";

import { gerarSlug } from "../utils/slug";
import { fmtMoeda } from "../utils/format";
import { PedidosOnline } from "./PedidosOnline";
import { ConversasLoja } from "./ConversasLoja";
import { categoriasDoProduto } from "../utils/categoriasProduto";
import { espelharLojaPublica } from "../utils/lojaPublica";

export function Site({ notificacaoDestino }) {
  const [view, setView] = useState("vitrine"); // "vitrine" | "pedidos" | "mensagens"
  useEffect(() => {
    if (notificacaoDestino?.tab === "site" && ["pedidos", "mensagens"].includes(notificacaoDestino.secao)) {
      setView(notificacaoDestino.secao);
    }
  }, [notificacaoDestino]);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCat, setFiltroCat] = useState("todas");
  const [busca, setBusca] = useState("");
  const [uid, setUid] = useState(null);
  const [slug, setSlug] = useState(null);
  const [editDescricao, setEditDescricao] = useState(null);
  const [savingDescription, setSavingDescription] = useState(false);
  const [descricaoForm, setDescricaoForm] = useState("");
  const [pedidosNaoLidos, setPedidosNaoLidos] = useState(0);
  const [mensagensNaoLidas, setMensagensNaoLidas] = useState(0);
  const [modalAdicionar, setModalAdicionar] = useState(false);
  const [buscaAdicionar, setBuscaAdicionar] = useState("");
  const [filtroPopup, setFiltroPopup] = useState("disponiveis"); // disponiveis | ocultos
  // Logo específica da vitrine (opcional) — some pra logo da empresa
  // (Configurações → Conta) quando não cadastrada. Nome da loja continua
  // sendo sempre o nome da empresa, sem opção de trocar aqui.
  const [empresaNome, setEmpresaNome] = useState("");
  const [logoEmpresaUrl, setLogoEmpresaUrl] = useState("");
  const [logoSiteUrl, setLogoSiteUrl] = useState("");
  const [logoSiteFile, setLogoSiteFile] = useState(null);
  const [previewLogoSite, setPreviewLogoSite] = useState("");
  const [salvandoLogo, setSalvandoLogo] = useState(false);
  const fileLogoSiteRef = useRef();
  // Detectar UID do usuário logado
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("3dp_user")) ||
                   JSON.parse(sessionStorage.getItem("3dp_user"));
      if (user?.uid) setUid(user.uid);
    } catch {}
  }, []);

  // Gerar slug a partir do nome da empresa e registrar mapeamento público
  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        const d = snap.data() || {};
        const empresa = d.empresa || d.nome || "";
        setEmpresaNome(empresa);
        setLogoEmpresaUrl(d.logoUrl || "");
        setLogoSiteUrl(d.logoSiteUrl || "");
        setPreviewLogoSite(d.logoSiteUrl || "");
        const s = gerarSlug(empresa);
        if (s) {
          setSlug(s);
          await setDoc(doc(db, "lojas", s), { uid, empresa }, { merge: true });
        }
      } catch (e) {
        console.error("Erro ao gerar slug da loja:", e);
      }
    })();
  }, [uid]);

  const escolherLogoSite = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast("Selecione um arquivo de imagem.", "warning"); return; }
    setLogoSiteFile(f);
    setPreviewLogoSite(URL.createObjectURL(f));
  };

  // Salva a logo assim que escolhida (mesmo padrão do resto da tela — não
  // fica esperando um botão "Salvar" geral).
  useEffect(() => {
    if (!logoSiteFile || !uid) return;
    (async () => {
      setSalvandoLogo(true);
      try {
        const r = ref(storage, `users/${uid}/logoSite`);
        await uploadBytes(r, logoSiteFile);
        const url = await getDownloadURL(r);
        await updateDoc(doc(db, "users", uid), { logoSiteUrl: url });
        await espelharLojaPublica(uid, { empresa: empresaNome, logoUrl: logoEmpresaUrl, logoSiteUrl: url });
        setLogoSiteUrl(url);
        toast("Logo da loja atualizada ✓", "success");
      } catch (e) {
        console.error("Erro ao salvar logo do site:", e);
        toast("Erro ao salvar a logo", "error");
        setPreviewLogoSite(logoSiteUrl);
      } finally {
        setLogoSiteFile(null);
        setSalvandoLogo(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoSiteFile]);

  const removerLogoSite = async () => {
    if (!uid) return;
    setSalvandoLogo(true);
    try {
      await updateDoc(doc(db, "users", uid), { logoSiteUrl: "" });
      await espelharLojaPublica(uid, { empresa: empresaNome, logoUrl: logoEmpresaUrl, logoSiteUrl: "" });
      setLogoSiteUrl(""); setPreviewLogoSite("");
      toast("Logo da loja removida — voltou a usar a logo da empresa.", "info");
    } catch (e) {
      console.error("Erro ao remover logo do site:", e);
      toast("Erro ao remover a logo", "error");
    } finally { setSalvandoLogo(false); }
  };

  // Contar pedidos não lidos (badge em tempo real)
  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, `users/${uid}/pedidos_online`),
      where("naoLidoVendedor", "==", true)
    );
    const unsub = onSnapshot(q, (snap) => setPedidosNaoLidos(snap.size), () => {});
    return () => unsub();
  }, [uid]);

  // Conversas livres com clientes (chat da vitrine) não lidas
  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, `users/${uid}/conversas_loja`),
      where("naoLidoVendedor", "==", true)
    );
    const unsub = onSnapshot(q, (snap) => setMensagensNaoLidas(snap.size), () => {});
    return () => unsub();
  }, [uid]);

  // Carregar produtos + categorias
  const carregar = async () => {
    setLoading(true);
    try {
      const [prods, cats] = await Promise.all([
        fbList("produtos"),
        fbList("categorias").catch(() => []),
      ]);
      setProdutos(prods);
      setCategorias(cats);
    } catch (e) {
      console.error("Erro ao carregar produtos:", e);
      toast("Erro ao carregar produtos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  // Produtos já no site (sempre aparecem na lista de gerenciamento, mesmo ocultos)
  const noSite = produtos.filter(p => p.ativo !== false);
  // Produtos fora do site — só esses aparecem no popup "Adicionar produtos".
  // ocultoAdicionar separa quem realmente não vai ser publicado (não polui
  // a lista de quem ainda pode entrar), sem apagar o produto do catálogo.
  const foraDoSiteBusca = produtos
    .filter(p => p.ativo === false)
    .filter(p => !buscaAdicionar || p.nome?.toLowerCase().includes(buscaAdicionar.toLowerCase()));
  const foraDoSite = foraDoSiteBusca.filter(p => filtroPopup === "ocultos" ? !!p.ocultoAdicionar : !p.ocultoAdicionar);
  const ocultosPopupCount = foraDoSiteBusca.filter(p => p.ocultoAdicionar).length;

  const listaFiltrada = noSite
    .filter(p => filtroCat === "todas" || categoriasDoProduto(p).includes(filtroCat))
    .filter(p => !busca || p.nome?.toLowerCase().includes(busca.toLowerCase()));

  const presentes = new Set(noSite.flatMap(categoriasDoProduto));
  const categoriasPresentes = [
    ...categorias.map(c => c.nome).filter(nome => presentes.has(nome)),
    ...[...presentes].filter(nome => !categorias.some(c => c.nome === nome)),
  ].sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity:"base" }));

  const adicionarAoSite = async (produto) => {
    try {
      await fbUpdate("produtos", produto.id, { ativo: true });
      setProdutos(p => p.map(pr => pr.id === produto.id ? { ...pr, ativo: true } : pr));
      toast(`"${produto.nome}" adicionado ao site ✓`, "success");
    } catch (e) {
      console.error("Erro ao adicionar:", e);
      toast("Erro ao adicionar produto", "error");
    }
  };

  const removerDoSite = async (id) => {
    try {
      await fbUpdate("produtos", id, { ativo: false });
      setProdutos(p => p.map(prod => prod.id === id ? { ...prod, ativo: false } : prod));
      toast("Removido do site", "info");
    } catch (e) {
      console.error("Erro ao atualizar:", e);
      toast("Erro ao atualizar produto", "error");
    }
  };

  const toggleOcultoAdicionar = async (id, ocultoAdicionar) => {
    try {
      await fbUpdate("produtos", id, { ocultoAdicionar });
      setProdutos(p => p.map(prod => prod.id === id ? { ...prod, ocultoAdicionar } : prod));
    } catch (e) {
      console.error("Erro ao atualizar:", e);
      toast("Erro ao atualizar produto", "error");
    }
  };

  const salvarDescricao = async (id) => {
    if(savingDescription)return;
    setSavingDescription(true);
    try {
      await fbUpdate("produtos", id, { descricao: descricaoForm });
      setProdutos(p => p.map(prod => prod.id === id ? { ...prod, descricao: descricaoForm } : prod));
      setEditDescricao(null);
      toast("Descrição salva ✓", "success");
    } catch (e) {
      console.error("Erro ao salvar:", e);
      toast("Erro ao salvar descrição", "error");
    } finally {setSavingDescription(false);}
  };

  const lojaUrl = uid ? "https://printcontrol.netlify.app/loja/"+encodeURIComponent(slug||uid) : "";
  const copiarLink = async () => {try {await navigator.clipboard.writeText(lojaUrl);toast("Link da loja copiado!","success");}catch{toast("Não foi possível copiar. Abra a loja e copie o endereço.","warning");}};
  const photo = produto => produto.fotoBase64 || produto.fotos?.[0]?.url;
  const descricaoProduto = produtos.find(p=>p.id===editDescricao);
  return <div className="tool-module site-workspace">
    <section className="site-hero">
      <div className="site-hero-copy"><span className="tool-eyebrow">SUA LOJA ONLINE</span><h1>Uma vitrine para<br /><span>suas melhores ideias.</span></h1><p>Cuide dos produtos, acompanhe os pedidos e converse com seus clientes.</p></div>
      <div className="site-store-card"><span className="site-store-logo">{(previewLogoSite||logoEmpresaUrl)?<img src={previewLogoSite||logoEmpresaUrl} alt="Logo da loja" />:<HeaderIcon name="cube" size={32} />}</span><div><strong>{empresaNome||"Minha loja"}</strong><p>{uid?"Seu endereço está pronto para compartilhar":"Preparando o endereço da loja…"}</p></div>{lojaUrl&&<><a className="tool-button" href={lojaUrl} target="_blank" rel="noopener noreferrer">Abrir minha loja ↗</a><ToolButton secondary onClick={copiarLink}>Copiar link</ToolButton></>}</div>
    </section>
    <ToolSummary items={[{label:"Produtos na vitrine",value:loading?"—":noSite.length},{label:"Pedidos não lidos",value:pedidosNaoLidos||0},{label:"Mensagens não lidas",value:mensagensNaoLidas||0}]} />
    <div className="site-tabs" role="tablist" aria-label="Gerenciar loja">{[{id:"vitrine",label:"Vitrine",icon:"materiais",count:0},{id:"pedidos",label:"Pedidos",icon:"notas",count:pedidosNaoLidos},{id:"mensagens",label:"Mensagens",icon:"clientes",count:mensagensNaoLidas}].map(item=><button type="button" role="tab" key={item.id} id={"site-tab-"+item.id} aria-controls={"site-panel-"+item.id} aria-selected={view===item.id} onClick={()=>setView(item.id)}><HeaderIcon name={item.icon} size={19} /><span>{item.label}</span>{item.count>0&&<b>{item.count}</b>}</button>)}</div>
    <section role="tabpanel" id={"site-panel-"+view} aria-labelledby={"site-tab-"+view} className="site-tab-content">
      {view==="pedidos"?<PedidosOnline vendedorId={uid} />:view==="mensagens"?<ConversasLoja vendedorId={uid} />:<>
        <div className="site-identity"><div><h2>Identidade da loja</h2><p>{previewLogoSite?"Você está usando uma logo exclusiva na vitrine.":"A vitrine usa a logo da empresa. Você pode escolher outra aqui."}</p></div><div className="tool-actions"><ToolButton secondary onClick={()=>fileLogoSiteRef.current?.click()} disabled={salvandoLogo}>{salvandoLogo?"Salvando…":previewLogoSite?"Trocar logo":"Enviar logo"}</ToolButton>{previewLogoSite&&<ToolButton secondary onClick={removerLogoSite} disabled={salvandoLogo}>Usar logo da empresa</ToolButton>}</div><input ref={fileLogoSiteRef} type="file" accept="image/*" aria-label="Enviar logo da loja" style={{display:"none"}} onChange={escolherLogoSite} /></div>
        <ToolSearch value={busca} onChange={setBusca} placeholder="Pesquisar na vitrine"><ToolButton onClick={()=>{setModalAdicionar(true);setBuscaAdicionar("");setFiltroPopup("disponiveis");}}>+ Adicionar produtos</ToolButton></ToolSearch>
        {categoriasPresentes.length>0&&<ToolFilters options={[{value:"todas",label:"Todas as categorias"},...categoriasPresentes.map(value=>({value,label:value}))]} value={filtroCat} onChange={setFiltroCat} />}
        <div className="site-products">{loading?<ToolEmpty icon="materiais" title="Carregando sua vitrine…" />:!listaFiltrada.length?<ToolEmpty icon="materiais" title={noSite.length?"Nenhum produto encontrado":"Sua vitrine começa com uma peça"}>Adicione produtos do seu catálogo ou ajuste a busca.</ToolEmpty>:listaFiltrada.map(p=><article className="site-product" key={p.id}>
          <div className="site-product-photo">{photo(p)?<img src={photo(p)} alt={p.nome} loading="lazy" />:<HeaderIcon name="cube" size={48} />}<span className="site-live">Na vitrine</span></div>
          <div className="site-product-content"><div className="site-product-categories">{categoriasDoProduto(p).join(" · ")||"Sem categoria"}</div><h3>{p.nome}</h3><div className="site-product-price"><strong>{fmtMoeda(p.preco)}</strong><span>{Number(p.estoque)>0?p.estoque+" em estoque":"Sob encomenda"}</span></div><p className="site-product-description">{p.descricao||"Adicione detalhes, opções e prazos para ajudar o cliente a escolher."}</p><div className="tool-actions"><ToolButton secondary onClick={()=>{setEditDescricao(p.id);setDescricaoForm(p.descricao||"");}}>{p.descricao?"Editar descrição":"Adicionar descrição"}</ToolButton><ToolButton danger onClick={()=>removerDoSite(p.id)} aria-label={"Retirar "+p.nome+" da vitrine"}>Retirar</ToolButton></div></div>
        </article>)}</div>
        <p className="site-footnote">Retirar da vitrine mantém o produto no seu catálogo. Você pode adicioná-lo novamente quando quiser.</p>
      </>}
    </section>
    {modalAdicionar&&<ToolDialog title="Adicionar à vitrine" subtitle="Escolha os produtos que seus clientes poderão encontrar na loja." icon="materiais" color="#71e5ce" onClose={()=>setModalAdicionar(false)}>
      <div className="tool-module"><ToolSearch value={buscaAdicionar} onChange={setBuscaAdicionar} placeholder="Pesquisar no catálogo" /><ToolFilters value={filtroPopup} onChange={setFiltroPopup} options={[{value:"disponiveis",label:"Disponíveis"},{value:"ocultos",label:"Ocultos da seleção",count:ocultosPopupCount}]} />
        <div className="site-products">{!foraDoSite.length?<ToolEmpty icon="materiais" title="Nenhum produto nesta seleção">Os produtos podem já estar na vitrine. Confira a busca e os ocultos.</ToolEmpty>:foraDoSite.map(p=><article key={p.id} className="site-product"><div className="site-product-photo">{photo(p)?<img src={photo(p)} alt={p.nome} loading="lazy" />:<HeaderIcon name="cube" size={42} />}</div><div className="site-product-content"><h3>{p.nome}</h3><strong>{fmtMoeda(p.preco)}</strong><div className="tool-actions"><ToolButton onClick={()=>adicionarAoSite(p)}>Adicionar à vitrine</ToolButton><ToolButton secondary onClick={()=>toggleOcultoAdicionar(p.id,filtroPopup!=="ocultos")}>{filtroPopup==="ocultos"?"Mostrar na seleção":"Ocultar da seleção"}</ToolButton></div></div></article>)}</div>
      </div>
    </ToolDialog>}
    {descricaoProduto&&<ToolDialog compact title="Descrição do produto" subtitle={descricaoProduto.nome} icon="notas" color="#71e5ce" onClose={()=>setEditDescricao(null)} busy={savingDescription}><label className="tool-textarea-label">O que o cliente precisa saber?<textarea rows={7} value={descricaoForm} onChange={e=>setDescricaoForm(e.target.value)} placeholder="Conte sobre o material, tamanho, cores e prazo de produção." /></label><div className="tool-actions"><ToolButton onClick={()=>salvarDescricao(editDescricao)} disabled={savingDescription}>{savingDescription?"Salvando…":"Salvar descrição"}</ToolButton><ToolButton secondary disabled={savingDescription} onClick={()=>setEditDescricao(null)}>Cancelar</ToolButton></div></ToolDialog>}
  </div>;
}
